import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  BanlistPrediction,
  BanlistPredictionBase,
  BanlistPredictionCardInput,
  BanlistPredictionLeaderboard,
  BanlistPredictionSummary,
  Nullable,
  PublicBanlistPrediction,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

// Predicciones de la próxima banlist global. El armado y el guardado piden
// sesión; ver una por link y el ranking, no.

const PREDICTION_FIELDS = gql`
  fragment BanlistPredictionFields on BanlistPrediction {
    id
    status
    shareHash
    baseBanlistId
    lastEditedAt
    cards {
      konamiId
      name
      image
      frameType
      status
    }
  }
`;

export const getMyBanlistPrediction = (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<Nullable<BanlistPrediction>>>(gql`
    ${PREDICTION_FIELDS}
    query myBanlistPrediction {
      data: myBanlistPrediction {
        ...BanlistPredictionFields
      }
    }
  `);

export const getMyBanlistPredictionHistory = (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<BanlistPredictionSummary[]>>(gql`
    query myBanlistPredictionHistory {
      data: myBanlistPredictionHistory {
        id
        shareHash
        score
        exactHits
        lastEditedAt
        targetBanlist {
          id
          name
        }
      }
    }
  `);

export const saveBanlistPrediction = (
  user: Nullable<UserWithToken>,
  cards: BanlistPredictionCardInput[]
) =>
  getGraphQLQueryClient(user).request<WithData<BanlistPrediction>>(
    gql`
      ${PREDICTION_FIELDS}
      mutation saveBanlistPrediction($cards: [BanlistPredictionCardInput!]!) {
        data: saveBanlistPrediction(cards: $cards) {
          ...BanlistPredictionFields
        }
      }
    `,
    { cards }
  );

export const newBanlistPrediction = (
  user: Nullable<UserWithToken>,
  copyFromId?: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<BanlistPrediction>>(
    gql`
      ${PREDICTION_FIELDS}
      mutation newBanlistPrediction($copyFromId: Int) {
        data: newBanlistPrediction(copyFromId: $copyFromId) {
          ...BanlistPredictionFields
        }
      }
    `,
    { copyFromId: copyFromId ?? null }
  );

/**
 * La base de la predicción: la última global de Advanced por fecha efectiva,
 * aunque esté anunciada y todavía no vigente. Pública.
 */
export const getBanlistPredictionBase = () =>
  getGraphQLQueryClient(null).request<WithData<Nullable<BanlistPredictionBase>>>(gql`
    query publicBanlistPredictionBase {
      data: publicBanlistPredictionBase {
        id
        name
        effectiveDate
        cards {
          konamiId
          name
          image
          frameType
          status
        }
      }
    }
  `);

export const getPublicBanlistPrediction = (shareHash: string) =>
  getGraphQLQueryClient(null).request<
    WithData<Nullable<PublicBanlistPrediction>>
  >(
    gql`
      query publicBanlistPrediction($shareHash: String!) {
        data: publicBanlistPrediction(shareHash: $shareHash) {
          id
          status
          shareHash
          lastEditedAt
          position
          user {
            id
            name
            lastName
            picture
          }
          base {
            id
            name
          }
          target {
            id
            name
          }
          cards {
            konamiId
            name
            image
            frameType
            status
          }
          changes {
            cards {
              konamiId
              name
              image
              frameType
              from
              to
              movement
            }
          }
          result {
            score
            exactHits
            directionHits
            wrong
            unpredicted
            cards {
              konamiId
              name
              image
              frameType
              from
              predicted
              actual
              mark
              points
            }
          }
        }
      }
    `,
    { shareHash }
  );

export const getBanlistPredictionLeaderboard = (
  targetBanlistId?: Nullable<number>,
  page = 1
) =>
  getGraphQLQueryClient(null).request<WithData<BanlistPredictionLeaderboard>>(
    gql`
      query publicBanlistPredictionLeaderboard($targetBanlistId: Int, $page: Int) {
        data: publicBanlistPredictionLeaderboard(
          targetBanlistId: $targetBanlistId
          page: $page
        ) {
          target {
            id
            name
          }
          rounds {
            id
            name
            entries
          }
          total
          entries {
            position
            shareHash
            score
            exactHits
            lastEditedAt
            user {
              id
              name
              lastName
              picture
            }
          }
        }
      }
    `,
    { targetBanlistId: targetBanlistId ?? null, page }
  );
